/* -------------------------------------------------------------------------- */
/*                                  Packages                                  */
/* -------------------------------------------------------------------------- */
import * as React from "react"
import { Address, getAddress, Logo } from "@abmg-components"
import { Link } from "gatsby"
import { isIOS } from "react-device-detect"
import { useDispatch } from "react-redux"
import { AnimatePresence, motion } from "framer-motion"

/* -------------------------------------------------------------------------- */
/*                                    Redux                                   */
/* -------------------------------------------------------------------------- */
import { update } from "@state/showCookieSettingsSlice"

/* -------------------------------------------------------------------------- */
/*                                    Data                                    */
/* -------------------------------------------------------------------------- */
import siteData from "@data/site.json"

/* -------------------------------------------------------------------------- */
/*                                    Hooks                                   */
/* -------------------------------------------------------------------------- */
import useClientSideOnly from "@hooks/use-client-side-only"

/* -------------------------------------------------------------------------- */
/*                             External Components                            */
/* -------------------------------------------------------------------------- */
import Disclosure from "@components/global/disclosures"
import ABIModal from "@components/global/abi-modal"
import ACTModal from "@components/global/act-modal"

/* -------------------------------------------------------------------------- */
/*                               External Utils                               */
/* -------------------------------------------------------------------------- */
import { homeownerHubLink } from "@utils/pillar-links"

/* -------------------------------------------------------------------------- */
/*                                 Components                                 */
/* -------------------------------------------------------------------------- */
type LinkSectionProps = {
  heading: string
  links: Array<InternalLinkProps | ExternalLinkProps | ButtonProps>
}
const LinkSection = (props: LinkSectionProps) => {
  return (
    <div className="flex flex-col gap-[12px]">
      <p className="font-bold leading-[normal] [margin-block-end:0] text-[12px] text-[#EDF2F7] uppercase">
        {props.heading}
      </p>
      <ul className="flex flex-col gap-[12px]">
        {props.links.map((link, i) => {
          const typedExternalLink = link as ExternalLinkProps
          const typedInternalLink = link as InternalLinkProps
          const typedLink = link as SharedLinkProps
          const typedButton = link as ButtonProps
          const LinkTag = typedInternalLink.to
            ? Link
            : typedExternalLink.href
              ? "a"
              : "button"
          return (
            <li
              key={`brand-link-${i}`}
              className="list_item--reset !leading-[normal]  [&_*]:leading-[20px] [&_*]:text-[14px] [&_*]:text-left"
            >
              <LinkTag
                to={typedInternalLink.to}
                href={`${
                  typedExternalLink.type === "email"
                    ? "mailto:"
                    : typedExternalLink.type === "phone"
                      ? "tel:"
                      : typedExternalLink.type === "address"
                        ? isIOS
                          ? `https://maps.apple.com/?address=`
                          : `https://google.com/maps/place/`
                        : ""
                }${typedExternalLink.href}`}
                {...typedButton.buttonFunctions}
                target={typedExternalLink.href ? "_blank" : undefined}
              >
                {typedLink.text}
              </LinkTag>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*                                    Props                                   */
/* -------------------------------------------------------------------------- */

interface SharedLinkProps {
  text: string | React.ReactNode
}
interface ButtonProps extends SharedLinkProps {
  buttonFunctions: {
    onClick?: React.MouseEventHandler<HTMLButtonElement>
  }
}
interface ExternalLinkProps extends SharedLinkProps {
  href: string
  type?: "address" | "email" | "phone"
}
interface InternalLinkProps extends SharedLinkProps {
  to: string
}

type SocialProps = {
  href: string
  type: "facebook" | "instagram" | "linkedin" | "zillow" | "twitter" | "youtube"
}
type Props = {
  brand: {
    linkSection?: LinkSectionProps
    logo?: React.ReactNode | string
  }
  disclosure: string
  mainLinkSections: Array<LinkSectionProps>
  socials: Array<SocialProps | null>
  variants?: {
    useAffiliationsBanner?: boolean
    type: "corporate" | "mb" | "other" | "mb-other"
  }
}

/* -------------------------------------------------------------------------- */
/*                              Default Component                             */
/* -------------------------------------------------------------------------- */
export default function (props: Props) {
  const [isABIModalActive, ABIToggleModal] = React.useState(false)
  const [isACTModalActive, ACTToggleModal] = React.useState(false)
  const isMounted = useClientSideOnly()
  const corporateFooter: Props = {
    brand: {
      linkSection: {
        heading: `Headquarters`,
        links: [
          {
            href: siteData.officePhone,
            text: siteData.officePhone,
            type: "phone",
          } as ExternalLinkProps,
          {
            href: siteData.fullAddress,
            text: (
              <Address streetBreak={true} cityBreak={true} suiteComma={true} />
            ),
            type: "address",
          } as ExternalLinkProps,
        ],
      },
    },
    disclosure: props.disclosure,
    mainLinkSections: [
      {
        heading: `Careers`,
        links: [
          {
            href: `https://www.joinatlanticbay.com`,
            text: `Sales Careers`,
          } as ExternalLinkProps,
          {
            to: `/internships/`,
            text: `Internships`,
          } as InternalLinkProps,
          {
            to: `/joint-venture/`,
            text: `Joint Venture`,
          } as InternalLinkProps,
        ],
      },
      {
        heading: `Affiliates`,
        links: [
          {
            buttonFunctions: {
              onClick: () => ABIToggleModal(true),
            },
            text: `AB Insurance`,
          } as ButtonProps,
          {
            buttonFunctions: {
              onClick: () => ACTToggleModal(true),
            },
            text: `Atlantic Carolinas Title`,
          } as ButtonProps,
        ],
      },
      {
        heading: `Programs`,
        links: [
          {
            to: `/builders/`,
            text: `Builders`,
          } as InternalLinkProps,
          {
            to: `/works/`,
            text: `AB Works`,
          } as InternalLinkProps,
          {
            to: `/tu-hogar-tu-futuro/`,
            text: `Tu Hogar, Tu Futuro`,
          } as InternalLinkProps,
        ],
      },
    ],
    socials: [
      {
        href: siteData.socials.facebook,
        type: "facebook",
      },
      {
        href: siteData.socials.instagram,
        type: "instagram",
      },
      {
        href: siteData.socials.linkedin,
        type: "linkedin",
      },
      {
        href: siteData.socials.youtube,
        type: "youtube",
      },
    ],
    variants: {
      useAffiliationsBanner: true,
      type: "corporate",
      ...props.variants,
    },
  }

  let mbFooter: Props = {
    brand: {
      linkSection: {
        heading: `Contact`,
        links: [
          {
            href:
              isMounted && props.bankerData?.phone
                ? props.bankerData.phone
                : "",
            text:
              isMounted && props.bankerData?.phone
                ? props.bankerData.phoneText
                : "",
            type: "phone",
          } as ExternalLinkProps,
          {
            href:
              isMounted && props.bankerData?.email
                ? props.bankerData.email
                : "",
            text:
              isMounted && props.bankerData?.email
                ? props.bankerData.email
                : "",
            type: "email",
          } as ExternalLinkProps,
          {
            href:
              isMounted && props.bankerData?.street
                ? getAddress(props.bankerData)
                : "",
            text: (
              <>
                <Address
                  professional={props.bankerData}
                  streetBreak={true}
                  cityBreak={props.bankerData?.suite && true}
                  cityComma={!props.bankerData?.suite && true}
                  suiteComma={props.bankerData?.suite && true}
                />
              </>
            ),
            type: "address",
          } as ExternalLinkProps,
        ],
      },
    },
    disclosure: props.disclosure,
    mainLinkSections: [
      {
        heading: `Resources`,
        links: [
          {
            text: `Homeowner Hub`,
            to: homeownerHubLink,
          },
          {
            text: `Calculator`,
            to:
              isMounted && props.bankerData?.slug
                ? `/${props.bankerData.slug}/#calculator`
                : "",
          },
          {
            text: `Knowledge Center`,
            to: `/knowledge-center/`,
          },
        ],
      },
      ...corporateFooter.mainLinkSections.filter(
        linkSection => linkSection.heading !== "Careers",
      ),
    ],
    socials: [
      isMounted && props.bankerData?.facebook
        ? {
            href: props.bankerData.facebook,
            type: "facebook",
          }
        : null,
      isMounted && props.bankerData?.instagram
        ? {
            href: props.bankerData.instagram,
            type: "instagram",
          }
        : null,
      isMounted && props.bankerData?.linkedin
        ? {
            href: props.bankerData.linkedin,
            type: "linkedin",
          }
        : null,
      isMounted && props.bankerData?.zillow
        ? {
            href: props.bankerData.zillow,
            type: "zillow",
          }
        : null,
      isMounted && props.bankerData?.twitter
        ? {
            href: props.bankerData.twitter,
            type: "twitter",
          }
        : null,
      isMounted && props.bankerData?.youtube
        ? {
            href: props.bankerData.youtube,
            type: "youtube",
          }
        : null,
    ],
    variants: {
      type: "mb",
      ...props.variants,
    },
  }

  let otherFooter: Props = {
    disclosure: props.disclosure,
    mainLinkSections: props.mainLinkSections
      ? props.mainLinkSections
      : [corporateFooter.brand.linkSection],
    socials: props.socials ? props.socials : corporateFooter.socials,
    variants: {
      type: "other",
      ...props.variants,
    },
  }
  let footerProps =
    props.variants?.type === "corporate"
      ? corporateFooter
      : props.variants?.type === "mb"
        ? mbFooter
        : props.variants?.type === "other"
          ? otherFooter
          : {
              disclosure: props.disclosure,
              mainLinkSections: [mbFooter.brand.linkSection],
              socials: mbFooter.socials,
            }

  const legalLinks = [
    {
      text: `Legal`,
      to: "/legal/",
    },
    {
      text: `Privacy Policy`,
      to: `/privacy-policy`,
    },
    {
      text: `Online Privacy Policy`,
      to: `/online-privacy-policy/`,
    },
    {
      buttonFunctions: {
        onClick: () => dispatch(update(true)),
      },
      text: `Cookie Settings`,
    },
    {
      text: `Opt-Out`,
      to: `/opt-out/`,
    },
  ]

  const dispatch = useDispatch()
  return (
    <>
      <footer className="bg-arctic-blue border border-[#E2E8F0] [padding:64px_24px_48px] [&_*]:text-grey-1 [&_hr]:border-b-grey-1 min-tablet1:[padding:64px_48px_48px] min-desktop1:[padding-inline:80px]">
        <div className="flex flex-col gap-[40px] justify-between [margin-inline:auto] max-w-[1278px]">
          {/* Top Portion */}
          <div className="flex flex-wrap gap-[40px] justify-between">
            {/* Left Side */}
            <div className="flex flex-col gap-[38px] max-w-[340px]">
              {/* Logo Container */}
              <div className="flex flex-col gap-[6px]">
                {footerProps.brand?.logo &&
                typeof footerProps.brand.logo !== "string" ? (
                  footerProps.brand.logo
                ) : (
                  <Logo
                    logo={
                      footerProps.brand?.logo &&
                      typeof footerProps.brand.logo === "string"
                        ? footerProps.brand?.logo
                        : "abmg-footer-logo-with-icon-white"
                    }
                  />
                )}
                <p className="leading-[22px] text-[13px] [margin-block-end:0] tracking-[.52px] uppercase">
                  Lending Peace of Mind since 1996.
                </p>
              </div>
              {footerProps.brand?.linkSection && (
                <LinkSection {...footerProps.brand.linkSection} />
              )}
            </div>
            {/* Right Side */}
            <div className="flex flex-wrap gap-[48px]">
              {footerProps.mainLinkSections.map(linkSection => {
                return (
                  <LinkSection key={linkSection.heading} {...linkSection} />
                )
              })}
            </div>
          </div>
          {/* Affiliations Banner */}
          {footerProps.variants?.useAffiliationsBanner && (
            <Link
              to="/professional-affiliations"
              className="bg-arctic-blue-4 flex flex-wrap gap-[10px] items-center justify-between [padding:31px_20px] rounded-[5px] min-tablet1:[padding-inline:28px] hover:no-underline min-desktop1:[padding-inline:60px] min-desktop1:grid min-desktop1:grid-cols-[minmax(200px,574px)_1fr]"
            >
              <p className="font-bold leading-[31px] [margin-block-end:0] max-w-[574px] text-[23px] !text-grey-3">
                Fostering, cultivating, and preserving a culture of diversity,
                equity, and inclusion.
              </p>
              <div className="flex flex-col gap-[10px] max-w-[463px] min-tablet1:flex-row min-tablet1:justify-between">
                <Logo logo="nahrep-white" />
                <Logo logo="nammba-white" />
                <Logo logo="charlotte-crown-white" />
              </div>
            </Link>
          )}
          <hr />
          <div className="[&_*]:leading-[18px] [&_*]:text-[12px] [&_p:last-child]:[margin-block-end:0] ">
            <Disclosure disclosure={footerProps.disclosure} />
            {/* <Logo logo="equal-opportunity-lender" /> */}
          </div>

          <hr />
          {/* Bottom Container */}
          <div className="grid gap-[16px] min-desktop3:grid-cols-[repeat(3,auto)] min-desktop3:justify-between">
            {footerProps.socials && (
              <div className="flex gap-[11px] [&_*]:transition-all">
                {footerProps.socials.map(social => {
                  if (!social) return null
                  return (
                    <a
                      className="group/link [&_path[fill='#F7FAFC']]:hover:fill-baltic-blue [&_path[fill='#031D30']]:hover:fill-grey-1"
                      href={social.href}
                      target="_blank"
                    >
                      {social.type === "facebook" ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                        >
                          <path
                            className="group-hover/link:fill-baltic-blue"
                            d="M19.9932 10.0002C19.9932 15.0508 16.2505 19.2262 11.3884 19.9038C10.9338 19.9668 10.4687 19.9998 9.99659 19.9998C9.45161 19.9998 8.91644 19.9563 8.39528 19.8722C3.6355 19.1057 0 14.9772 0 10.0002C0 4.4774 4.47608 0 9.99729 0C15.5185 0 19.9946 4.4774 19.9946 10.0002H19.9932Z"
                            fill="#F7FAFC"
                          />
                          <path
                            className="group-hover/link:fill-grey-1"
                            d="M11.3884 8.02957V10.208H14.0825L13.6559 13.1425H11.3884V19.9034C10.9338 19.9665 10.4687 19.9994 9.99656 19.9994C9.45158 19.9994 8.91642 19.956 8.39526 19.8719V13.1425H5.91064V10.208H8.39526V7.5426C8.39526 5.88897 9.73528 4.54785 11.3891 4.54785V4.54925C11.394 4.54925 11.3982 4.54785 11.4031 4.54785H14.0832V7.08575H12.332C11.8115 7.08575 11.3891 7.50827 11.3891 8.02888L11.3884 8.02957Z"
                            fill="#031D30"
                          />
                        </svg>
                      ) : social.type === "instagram" ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                        >
                          <path
                            className="group-hover/link:fill-baltic-blue"
                            d="M10.0197 0H9.97417C4.46559 0 0 4.4669 0 9.97711V10.0226C0 15.5329 4.46559 19.9998 9.97417 19.9998H10.0197C15.5283 19.9998 19.9939 15.5329 19.9939 10.0226V9.97711C19.9939 4.4669 15.5283 0 10.0197 0Z"
                            fill="#F7FAFC"
                          />
                          <path
                            className="group-hover/link:fill-grey-1"
                            d="M13.091 4.06934H6.90432C5.19515 4.06934 3.80469 5.4602 3.80469 7.16988V12.8314C3.80469 14.5411 5.19515 15.932 6.90432 15.932H13.091C14.8002 15.932 16.1906 14.5411 16.1906 12.8314V7.16988C16.1906 5.4602 14.8002 4.06934 13.091 4.06934ZM4.89814 7.16988C4.89814 6.0635 5.79826 5.16311 6.90432 5.16311H13.091C14.197 5.16311 15.0972 6.0635 15.0972 7.16988V12.8314C15.0972 13.9378 14.197 14.8382 13.091 14.8382H6.90432C5.79826 14.8382 4.89814 13.9378 4.89814 12.8314V7.16988Z"
                            fill="#031D30"
                          />
                          <path
                            className="group-hover/link:fill-grey-1"
                            d="M9.99818 12.8843C11.5876 12.8843 12.8814 11.5908 12.8814 10.0002C12.8814 8.40968 11.5883 7.11621 9.99818 7.11621C8.40808 7.11621 7.11499 8.40968 7.11499 10.0002C7.11499 11.5908 8.40808 12.8843 9.99818 12.8843ZM9.99818 8.21069C10.9852 8.21069 11.7879 9.01367 11.7879 10.0009C11.7879 10.9882 10.9852 11.7912 9.99818 11.7912C9.0112 11.7912 8.20844 10.9882 8.20844 10.0009C8.20844 9.01367 9.0112 8.21069 9.99818 8.21069Z"
                            fill="#031D30"
                          />
                          <path
                            className="group-hover/link:fill-grey-1"
                            d="M13.1484 7.58148C13.5764 7.58148 13.9253 7.23323 13.9253 6.80441C13.9253 6.37559 13.5771 6.02734 13.1484 6.02734C12.7197 6.02734 12.3716 6.37559 12.3716 6.80441C12.3716 7.23323 12.7197 7.58148 13.1484 7.58148Z"
                            fill="#031D30"
                          />
                        </svg>
                      ) : social.type === "linkedin" ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                        >
                          <path
                            className="group-hover/link:fill-baltic-blue"
                            d="M10.0197 0H9.97418C4.4656 0 0 4.4669 0 9.97711V10.0227C0 15.5329 4.4656 19.9998 9.97418 19.9998H10.0197C15.5283 19.9998 19.9939 15.5329 19.9939 10.0227V9.97711C19.9939 4.4669 15.5283 0 10.0197 0Z"
                            fill="#F7FAFC"
                          />
                          <path
                            className="group-hover/link:fill-grey-1"
                            d="M4.7679 6.6481C4.50452 6.40356 4.37354 6.10087 4.37354 5.74072C4.37354 5.38056 4.50522 5.06455 4.7679 4.81931C5.03128 4.57477 5.37032 4.45215 5.7857 4.45215C6.20109 4.45215 6.52682 4.57477 6.7895 4.81931C7.05288 5.06385 7.18386 5.37145 7.18386 5.74072C7.18386 6.10998 7.05218 6.40356 6.7895 6.6481C6.52612 6.89265 6.19198 7.01526 5.7857 7.01526C5.37942 7.01526 5.03128 6.89265 4.7679 6.6481ZM6.96252 8.05089V15.5483H4.59418V8.05089H6.96252Z"
                            fill="#031D30"
                          />
                          <path
                            className="group-hover/link:fill-grey-1"
                            d="M14.8469 8.79102C15.3632 9.35157 15.621 10.1209 15.621 11.1005V15.4153H13.3717V11.4046C13.3717 10.9106 13.2436 10.5266 12.9879 10.2534C12.7322 9.98009 12.3876 9.84276 11.9561 9.84276C11.5246 9.84276 11.1799 9.97939 10.9242 10.2534C10.6686 10.5266 10.5404 10.9106 10.5404 11.4046V15.4153H8.27783V8.02937H10.5404V9.00893C10.7694 8.68241 11.0784 8.42457 11.4664 8.23468C11.8545 8.04479 12.2909 7.9502 12.7763 7.9502C13.6407 7.9502 14.3314 8.23047 14.8469 8.79032V8.79102Z"
                            fill="#031D30"
                          />
                        </svg>
                      ) : social.type === "zillow" ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                        >
                          <g clip-path="url(#clip0_1262_11338)">
                            <path
                              className="group-hover/link:fill-grey-1"
                              d="M9.995 19.99C15.5151 19.99 19.99 15.5151 19.99 9.995C19.99 4.47491 15.5151 0 9.995 0C4.47491 0 0 4.47491 0 9.995C0 15.5151 4.47491 19.99 9.995 19.99Z"
                              fill="#F7FAFC"
                            />
                            <path
                              className="group-hover/link:fill-baltic-blue"
                              d="M11.8174 6.52449C11.8691 6.51372 11.8906 6.53095 11.9229 6.56542C12.1017 6.76359 12.6747 7.46152 12.8298 7.65324C12.8578 7.6877 12.8384 7.72432 12.8147 7.74586C11.6601 8.64627 10.3763 9.9215 9.65898 10.8348C9.64606 10.8542 9.65683 10.8542 9.66975 10.8521C10.9148 10.3135 13.8465 9.45621 15.1649 9.21711V7.54553L9.99502 3.44629L4.82735 7.54338V9.37005C6.43 8.41794 10.1372 6.94238 11.8174 6.52449ZM7.61259 13.5447C7.59514 13.5563 7.57397 13.561 7.55324 13.5578C7.53252 13.5546 7.51373 13.5438 7.50058 13.5275L6.5377 12.3793C6.51185 12.347 6.50754 12.3298 6.54201 12.2802C7.28733 11.1881 8.80812 9.48852 9.77746 8.76906C9.79685 8.75829 9.79038 8.74321 9.76884 8.75182C8.75857 9.0814 5.88716 10.3179 4.8252 10.9167V14.8199H15.1627V11.061C13.7539 11.3023 9.53405 12.5581 7.61259 13.5447Z"
                              fill="#031D30"
                            />
                          </g>
                          <defs>
                            <clipPath id="clip0_1262_11338">
                              <rect width="19.99" height="19.99" fill="white" />
                            </clipPath>
                          </defs>
                        </svg>
                      ) : social.type === "twitter" ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                        >
                          <path
                            d="M19.9886 9.99795C19.9886 15.0474 16.2468 19.2218 11.3858 19.8992C10.9313 19.9623 10.4663 19.9952 9.9943 19.9952C9.44945 19.9952 8.9144 19.9518 8.39336 19.8677C3.63467 19.1013 0 14.9738 0 9.99795C0 4.47638 4.47506 0 9.995 0C15.5149 0 19.99 4.47638 19.99 9.99795H19.9886Z"
                            fill="#F7FAFC"
                          />
                          <path
                            d="M4.05313 4.40869L8.66265 10.5733L4.02441 15.5856H5.0686L9.12977 11.1975L12.4108 15.5856H15.9635L11.0949 9.07421L15.4124 4.40869H14.3682L10.6285 8.45004L7.60657 4.40869H4.05382H4.05313ZM5.58823 5.17787H7.21999L14.427 14.8164H12.7953L5.58823 5.17787Z"
                            fill="#031D30"
                          />
                        </svg>
                      ) : social.type === "youtube" ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                        >
                          <path
                            d="M19.99 9.99795C19.99 4.47623 15.5151 0 9.99501 0C4.47492 0 0 4.47623 0 9.99795C0 15.5197 4.47492 19.9959 9.99501 19.9959C15.5151 19.9959 19.99 15.5197 19.99 9.99795Z"
                            fill="#F7FAFC"
                          />
                          <path
                            d="M16.2885 7.92653C16.2098 7.16708 16.0405 6.32754 15.4176 5.88634C14.9351 5.5442 14.3008 5.53155 13.7088 5.53225C12.4572 5.53225 11.2049 5.53436 9.95335 5.53506C8.74954 5.53647 7.54573 5.53717 6.34191 5.53858C5.83904 5.53858 5.35021 5.49993 4.88315 5.71772C4.48212 5.9046 4.16817 6.26009 3.97924 6.65563C3.71727 7.20572 3.66249 7.82888 3.63088 8.43729C3.57259 9.5452 3.57891 10.6559 3.64844 11.7631C3.69971 12.5711 3.82964 13.464 4.45402 13.979C5.00747 14.4349 5.78847 14.4574 6.50626 14.4581C8.78465 14.4602 11.0638 14.4623 13.3428 14.4637C13.635 14.4644 13.9398 14.4588 14.2376 14.4265C14.8234 14.3633 15.3817 14.1954 15.7582 13.7612C16.1382 13.3235 16.2358 12.7144 16.2934 12.1376C16.4338 10.7381 16.4324 9.3253 16.2885 7.92653ZM8.63576 11.9599V8.03613L12.0323 9.99764L8.63576 11.9599Z"
                            fill="#031D30"
                          />
                        </svg>
                      ) : null}
                    </a>
                  )
                })}
              </div>
            )}
            {/* Legal Links */}
            <ul className="flex flex-wrap gap-[16px]">
              {legalLinks.map((link, i) => {
                const typedExternalLink = link as ExternalLinkProps
                const typedInternalLink = link as InternalLinkProps
                const typedLink = link as SharedLinkProps
                const typedButton = link as ButtonProps
                const LinkTag = typedInternalLink.to
                  ? Link
                  : typedExternalLink.href
                    ? "a"
                    : "button"
                return (
                  <React.Fragment key={`${link.text}`}>
                    <li className="list_item--reset flex gap-[16px] !leading-[normal] w-fit [&_*]:leading-[normal] [&_*]:text-[13px]">
                      <LinkTag
                        to={typedInternalLink.to}
                        href={`${
                          typedExternalLink.type === "email"
                            ? "mailto:"
                            : typedExternalLink.type === "phone"
                              ? "tel:"
                              : typedExternalLink.type === "address"
                                ? isIOS
                                  ? `https://maps.apple.com/?address=`
                                  : `https://google.com/maps/place/`
                                : ""
                        }${typedExternalLink.href}`}
                        {...typedButton.buttonFunctions}
                        target={typedExternalLink.href ? "_blank" : undefined}
                      >
                        {typedLink.text}
                      </LinkTag>
                      {i < legalLinks.length - 1 && <span>|</span>}
                    </li>
                  </React.Fragment>
                )
              })}
            </ul>
            <p className="font-semibold leading-[normal] [margin-block-end:0] text-[13px]">
              © {new Date().getFullYear()} Atlantic Bay Mortgage Group
            </p>
          </div>
        </div>
      </footer>
      {/* POP-UP/MODAL FOR ABI */}
      <AnimatePresence>
        {isABIModalActive && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <ABIModal toggleModal={ABIToggleModal} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* POP-UP/MODAL FOR ABI */}
      <AnimatePresence>
        {isACTModalActive && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <ACTModal toggleModal={ACTToggleModal} />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
